import type {
  LoadingOperation,
  LoadingOperationToken,
  LoadingState,
  LoadingStore,
  StartLoadingOptions,
} from './types'

const createToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `loading-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const buildSnapshot = (operations: Map<LoadingOperationToken, LoadingOperation>): LoadingState => {
  const values = Array.from(operations.values()).sort((left, right) => left.startedAt - right.startedAt)
  return {
    isLoading: values.length > 0,
    isBlocking: values.some((operation) => operation.blocking),
    activeCount: values.length,
    operations: values,
  }
}

export function createLoadingStore(): LoadingStore {
  const listeners = new Set<() => void>()
  const operations = new Map<LoadingOperationToken, LoadingOperation>()
  let snapshot = buildSnapshot(operations)

  const notify = () => {
    snapshot = buildSnapshot(operations)
    listeners.forEach((listener) => listener())
  }

  const getSnapshot = () => snapshot

  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  const startLoading = (options: StartLoadingOptions = {}) => {
    const token = options.id || createToken()
    const operation: LoadingOperation = {
      id: token,
      blocking: Boolean(options.blocking),
      startedAt: options.startedAt ?? Date.now(),
      ...(options.label !== undefined ? { label: options.label } : {}),
      ...(options.type !== undefined ? { type: options.type } : {}),
      ...(options.source !== undefined ? { source: options.source } : {}),
    }
    operations.set(token, operation)
    notify()
    return token
  }

  const stopLoading = (token: LoadingOperationToken) => {
    if (!token) {
      return
    }
    const deleted = operations.delete(token)
    if (deleted) {
      notify()
    }
  }

  const withLoading = async <T>(
    fn: (() => Promise<T>) | Promise<T>,
    options: StartLoadingOptions = {}
  ): Promise<T> => {
    const token = startLoading(options)
    try {
      if (typeof fn === 'function') {
        return await fn()
      }
      return await fn
    } finally {
      stopLoading(token)
    }
  }

  return {
    get isLoading() {
      return getSnapshot().isLoading
    },
    get isBlocking() {
      return getSnapshot().isBlocking
    },
    get activeCount() {
      return getSnapshot().activeCount
    },
    get operations() {
      return getSnapshot().operations
    },
    getSnapshot,
    subscribe,
    startLoading,
    stopLoading,
    withLoading,
  }
}
